import type { Plan } from "../schemas";

type PlanIdentityCandidate = Partial<
	Pick<
		Plan,
		| "id"
		| "nombre"
		| "precio_mensual"
		| "precio_anual"
		| "proveedor_plan_id_mensual"
		| "proveedor_plan_id_anual"
	>
>;

const FREE_PLAN_NAME_ALIASES = ["free", "gratis"];

const normalizePlanName = (name?: string | null): string =>
	name?.trim().toLowerCase() ?? "";

export const isFreePlan = (plan?: PlanIdentityCandidate | null): boolean => {
	if (!plan) {
		return false;
	}

	const hasNoProviderIds =
		!plan.proveedor_plan_id_mensual && !plan.proveedor_plan_id_anual;
	const hasZeroPrice =
		Number(plan.precio_mensual ?? 0) === 0 && Number(plan.precio_anual ?? 0) === 0;

	if (hasNoProviderIds && hasZeroPrice) {
		return true;
	}

	const normalizedName = normalizePlanName(plan.nombre);
	return FREE_PLAN_NAME_ALIASES.some((alias) => normalizedName.includes(alias));
};

export const isPaidPlan = (plan?: PlanIdentityCandidate | null): boolean =>
	Boolean(plan) && !isFreePlan(plan);

export const findFreePlan = (plans?: Plan[] | null): Plan | null =>
	plans?.find((plan) => isFreePlan(plan)) ?? null;

/** The three tiers the pricing page renders, in upgrade order. */
export type PlanTier = "free" | "pro" | "investor";

const INVESTOR_NAME_ALIASES = ["investor", "inversionista"];

/**
 * The plan sold as "Pro": named "Profesional" or exactly "Pro". Not merely any
 * paid, non-investor plan, since other paid tiers exist (e.g. "Basico") that
 * the pricing page does not offer.
 */
export const isProPlan = (plan: PlanIdentityCandidate): boolean => {
	if (isFreePlan(plan)) return false;
	const normalizedName = normalizePlanName(plan.nombre);
	return normalizedName.includes("profesional") || normalizedName === "pro";
};

/** The plans the pricing page offers: Free, Pro and Investor. */
export const isOfferedPlan = (plan: PlanIdentityCandidate): boolean =>
	isFreePlan(plan) ||
	isProPlan(plan) ||
	INVESTOR_NAME_ALIASES.some((alias) => normalizePlanName(plan.nombre).includes(alias));

/**
 * Which tier a plan belongs to. The pricing page words each tier differently,
 * so the copy has to know the tier and not just the plan's name.
 */
export const getPlanTier = (plan: PlanIdentityCandidate): PlanTier => {
	if (isFreePlan(plan)) {
		return "free";
	}

	const normalizedName = normalizePlanName(plan.nombre);
	if (INVESTOR_NAME_ALIASES.some((alias) => normalizedName.includes(alias))) {
		return "investor";
	}

	return "pro";
};
